<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div
    class="menu-drawer critical-component-hide"
    :class="[
      `show-tier${currentTier}`,
      themeClass
    ]"
    :style="`
      --menu-drawer-bg-mobile: ${megaMenuTheme.mobile.background};
    `"
    data-gtm-mobile-nav
  >
    <!-- Menu drawer header -->
    <div class="menu-drawer__header">
      <div class="menu-drawer__header-content">
        <button
          v-if="!currentLink"
          type="button"
          class="menu-drawer-tab"
          :class="{ [$class('active')]: activeTab === 1 }"
          @click="(event) => handleTabClick(event, 1)"
        >
          <span
            class="menu-drawer__header-child"
            v-text="tab1Title"
          />
        </button>
        <button
          v-if="!currentLink"
          type="button"
          class="menu-drawer-tab"
          :class="{ [$class('active')]: activeTab === 2 }"
          @click="(event) => handleTabClick(event, 2)"
        >
          <span
            class="menu-drawer__header-child"
            v-text="tab2Title"
          />
        </button>
        <a
          v-if="$variable('customer.loggedIn')"
          class="menu-drawer-tab"
          :href="$variable('routes.account.url')"
          data-gtm-mobile-nav-level="primary"
          @click="((event) => handleGtm(event))"
        >
          <span
            class="menu-drawer__header-child"
            :data-title="$string('general.navigation.account')"
            v-text="$string('general.navigation.account')"
          />
        </a>
        <button
          v-else-if="!currentLink"
          type="button"
          class="menu-drawer-tab"
          data-gtm-mobile-nav-level="primary"
          @click="(event) => handleAccountTabClick(event)"
        >
          <span
            class="menu-drawer__header-child"
            v-text="$string('general.menu_drawer.account')"
          />
        </button>

        <button
          v-else
          class="menu-drawer__header-back menu-drawer__header-child"
          type="button"
          @click="(currentLink = false)"
        >
          <icon-chevron-left-center />
          <span
            class="menu-drawer__header-back-label"
            v-text="currentLink.label"
          />
        </button>
      </div>
    </div>

    <div class="menu-drawer__scroll">
      <!-- Navigation -->
      <div class="menu-drawer__navigation">
        <nav class="menu-drawer__menus">
          <!-- Tier 1 -->

          <div
            v-if="activeTab === 1"
            class="menu-drawer__menu menu-drawer__tier1"
            js-body-scroll-lock-ignore
            js-menu-tier="1"
            data-gtm-mobile-nav-level="secondary"
            :class="{ [$class('active')]: getActiveState(false) }"
          >
            <clickable-list
              ref="tier1Links"
              :links="clickableListContent"
              @link-clicked="handleLinkClick"
            />
          </div>

          <div
            v-if="activeTab === 2"
            class="menu-drawer__menu menu-drawer__tier1"
            js-menu-tier="1"
            js-body-scroll-lock-ignore
            data-gtm-mobile-nav-level="secondary"
            :class="{ [$class('active')]: getActiveState(false) }"
          >
            <clickable-list
              ref="tier1Links"
              :links="clickableListContent2"
              @link-clicked="handleLinkClick"
            />
          </div>

          <!-- Tier 2 -->
          <div
            v-if="currentLink?.links?.length"
            class="menu-drawer__menu menu-drawer__tier2"
            js-menu-tier="2"
            js-body-scroll-lock-ignore
            data-gtm-mobile-nav-level="tertiary"
            :class="{
              [$class('active')]: currentTier === 2
            }"
          >
            <clickable-list
              v-if="tierTwoContent.length"
              ref="tier2Links"
              :links="tierTwoContent"
            />
          </div>
        </nav>
      </div>

      <!-- Carousel -->
      <div class="menu-drawer__carousel">
        <menu-carousel
          :autoplay-speed="0"
          namespace="menu-drawer"
          :slides="slides"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Menu drawer
 * -----------------------------------------------------------------------------
 * Mobile menu drawer.
 * - CSS variable --menu-drawer-links-height is updated to match height of
 *   current visible links.
 *
 * @param {Boolean} isActive - If the containing overlay is active.
 * @param {Array} items - Menu content, linklist items.
 * @param {Number} itemCount - Number of tier 1 items in linklist.
 * @param {Object} megaMenuTheme - Theme settings for the mega menu.
 * @param {Array} slides - Array of mobile carousel slides.
 *
 */
import { nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import { formatLinklist } from '~/helpers/format'

import IconChevronLeftCenter from '~icons/direction/chevron/left-center.svg'

import ClickableList from '~global/clickable-list/clickable-list'
import MenuCarousel from '~async/menu-carousel/menu-carousel'

export default {
  name: 'MenuDrawer',

  components: {
    ClickableList,
    IconChevronLeftCenter,
    MenuCarousel,
  },

  props: {
    isActive: {
      type: Boolean,
      default: false,
    },

    items: {
      type: Array,
      default: () => [],
    },

    megaMenuTheme: {
      type: Object,
      default: () => ({
        desktop: {
          background: '',
          content: '',
        },
        mobile: {
          background: '',
          content: '',
        },
      }),
    },

    slides: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      activeTab: 1,
      currentLink: false,
      currentHeight: false,
      tier2: [],
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getFirstElement: 'accessibility/getFirstElement',
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Returns and interpolated theme class to use for theming.
     * @returns {String}
     */
    themeClass() {
      return this.isMobile
        ? `section-content--${this.megaMenuTheme.mobile.content}`
        : `section-content--${this.megaMenuTheme.desktop.content}`
    },

    tab1Title() {
      return this.items[0]?.title || this.$string('general.menu_drawer.shop')
    },

    tab2Title() {
      return this.items[1]?.title || this.$string('general.menu_drawer.explore')
    },

    /**
     * Returns a formatted list of linklist items for use in clickable-list
     * component.
     * @returns {Array}
     */
    clickableListContent() {
      return this.items[0]?.links?.map(linklist => formatLinklist(linklist))
    },

    clickableListContent2() {
      return this.items[1]?.links?.map(linklist => formatLinklist(linklist))
    },

    /**
     * Returns a formatted list of linklist items for use in clickable-list
     * component.
     * @returns {Array}
     */
    tierTwoContent() {
      if (!this.currentLink?.links?.length) {
        return []
      }
      return this.currentLink.links.map(links => formatLinklist(links))
    },

    /**
     * Compute back to top string (max-length issue).
     * @returns {String}
     */
    backToTopString() {
      return `${this.$string('general.menu_drawer.back_to')} ${this.$string('general.menu_drawer.top_level')}`
    },

    /**
     * Compute current tier to show.
     * @returns {Number}
     */
    currentTier() {
      if (!this.currentLink) {
        return 1
      }

      return this.currentLink.tier + 1
    },
  },

  watch: {

    /**
     * Updates drawer eight when link content changes.
     */
    clickableListContent() {
      this.updateHeight()
    },

    clickableListContent2() {
      this.updateHeight()
    },

    /**
     * Watch current link to update focus.
     */
    currentLink() {
      this.resetClickableLists()
      this.updateFocus()
      this.updateHeight()
    },

    /**
     * Watch active overlay to reset current link.
     */
    getActiveOverlay() {
      this.currentLink = false
      this.resetClickableLists()
      this.updateHeight()
    },

    /**
     * Watch isMobile to close on change.
     */
    isMobile() {
      if (this.isMobile) {
        return
      }

      this.closeOverlay('menuDrawer')
    },
  },

  mounted() {
    this.updateHeight()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      closeOverlay: 'overlays/close',
      setFocusTrap: 'accessibility/setFocusTrap',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Get active state for nested menu.
     * @param {Object} link - Link.
     * @returns {Boolean}
     */
    getActiveState(link) {
      return this.isActive && link.handle === this.currentLink.handle
    },

    /**
     * Resets both clickable lists to default state.
     */
    resetClickableLists() {
      this.$refs.tier1Links?.resetComponentData()
      this.$refs.tier2Links?.resetComponentData()
    },

    /**
     * Update focus trap and scroll lock.
     */
    updateFocus() {
      nextTick().then(() => {
        cnvs.EventBus.emit('overlay:update', 'menuDrawer')
      })
    },

    /**
     * Update height of current active navigation.
     * - Goes through each nav item and get height of element.
     */
    updateHeight() {
      nextTick().then(() => {
        let height = 0
        const parent = document.querySelector(`[js-menu-tier="${this.currentTier}"]`)
        const links = [...parent.querySelectorAll('.clickable-list__list-item')]

        links.forEach((link) => {
          if (!link?.offsetHeight) {
            return
          }

          height += link.offsetHeight
        })

        /**
         * Set CSS variable.
         */
        document.documentElement.style.setProperty(
          '--menu-drawer-links-height',
          `${height}px`,
        )

        /**
         * Update Vue data for easier debugging.
         */
        this.currentHeight = height
      })
    },

    /**
     * Handle link click.
     * @param {Object|Boolean} link - Clicked link object, or false.
     */
    handleLinkClick(link) {
      this.currentLink = link
    },

    handleTabClick(event, item) {
      event.preventDefault()
      this.activeTab = item
    },

    handleAccountTabClick(event) {
      this.handleGtm(event)

      window.location.assign(this.$variable('routes.account.url'))
    },

    handleGtm(event) {
      const dataLayer = window.dataLayer || []
      const target = event.currentTarget

      dataLayer.push({
        event: 'navigation_click_mobile',
        navigationClickMobile: {
          country: 'primary',
          click_url: target.href,
          click_text: target.innerText,
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import './menu-drawer';
</style>
